import dayjs from 'dayjs';
import { StatusVariant } from '@/components/ui/types/status-variant';
import {
  AlexaDeviceMeta,
  Device,
  DeviceType,
  HexNodeTabletMeta,
} from '@/types/device';

export const isAlexaDevice = (
  device: Device,
): device is Device & { type: 'ALEXA'; meta: AlexaDeviceMeta } =>
  device.type === 'ALEXA';

export const isHexNodeDevice = (
  device: Device,
): device is Device & { type: 'HEX_NODE_TABLET'; meta: HexNodeTabletMeta } =>
  device.type === 'HEX_NODE_TABLET';

export const getDeviceDisplayName = (device: Device): string =>
  device.meta?.displayName ??
  device.meta?.friendlyName ??
  device.meta?.serialNumber ??
  'Unknown Device';

const deviceTypeLabels: Record<DeviceType, string> = {
  ALEXA: 'Alexa',
  HEX_NODE_TABLET: 'HexNode Tablet',
};

export const getDeviceTypeLabel = (type: DeviceType): string =>
  deviceTypeLabels[type];

const TABLET_INACTIVE_AFTER_MINUTES = 3;
const TABLET_OFFLINE_AFTER_MINUTES = 5;

export type DeviceStatusDisplay = { text: string; variant: StatusVariant };

const getTabletStatusDisplay = (
  meta: HexNodeTabletMeta,
): DeviceStatusDisplay => {
  if (!meta.lastSeen) return { text: 'Offline', variant: 'alert' };

  const minutesSinceLastSeen = dayjs().diff(
    dayjs(meta.lastSeen),
    'minute',
    true,
  );

  if (minutesSinceLastSeen > TABLET_OFFLINE_AFTER_MINUTES)
    return { text: 'Offline', variant: 'alert' };
  if (minutesSinceLastSeen > TABLET_INACTIVE_AFTER_MINUTES)
    return { text: 'Inactive', variant: 'warning' };
  return { text: 'Online', variant: 'success' };
};

/**
 * Tablet devices derive their status from `meta.lastSeen` recency; all other
 * device types keep the existing connectivity.status-based display.
 */
export const getDeviceStatusDisplay = (device: Device): DeviceStatusDisplay => {
  if (isHexNodeDevice(device)) return getTabletStatusDisplay(device.meta);

  const connectivityStatus = device.state?.connectivity?.status ?? 'Unknown';
  return {
    text: connectivityStatus,
    variant: connectivityStatus === 'Online' ? 'success' : 'alert',
  };
};
